/**
 * Color / contrast utilities.
 *
 * Uses the WCAG 2.1 relative-luminance formula (NOT the older perceived
 * brightness approximation) so contrast decisions match accessibility
 * tooling like axe, Lighthouse, Stark, etc.
 *
 * WCAG spec: https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
 */

// Minimum contrast ratio for body text per WCAG 2.1 AA
export const WCAG_AA_NORMAL = 4.5;
export const WCAG_AA_LARGE = 3.0;

function hexToRgb(hex) {
  if (!hex || typeof hex !== "string") return null;
  const h = hex.replace("#", "").trim();
  if (h.length !== 6) return null;
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function channel(c) {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

/** WCAG relative luminance in [0, 1]. */
export function relativeLuminance(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b);
}

/** WCAG contrast ratio between two hex colors (≥ 1). */
export function contrastRatio(hex1, hex2) {
  const l1 = relativeLuminance(hex1);
  const l2 = relativeLuminance(hex2);
  const [hi, lo] = l1 >= l2 ? [l1, l2] : [l2, l1];
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Pick the text colour (white vs deep black) using PERCEIVED BRIGHTNESS
 * of the background — not WCAG contrast ratio.
 *
 * Rationale: saturated warm/mid colours (red, orange, green, yellow) look
 * muddy with white text even when white *technically* has the higher
 * contrast ratio. The human eye perceives those hues as "bright" and
 * wants black text on top. Threshold is set so Pulse (37.7%), Vitality
 * (36.4%) and Monocle (75.6%) all flip to black while Pavillion (32.6%)
 * and darker palettes keep white.
 *
 * Formula: YIQ perceived brightness — (R*0.299 + G*0.587 + B*0.114) / 255.
 *   > 0.36  → the bg reads as light/vivid → use #000000 (black)
 *   ≤ 0.36 → the bg reads as genuinely dark → use #FFFFFF (white)
 */
export function readableTextOn(hexBg) {
  const rgb = hexToRgb(hexBg);
  if (!rgb) return "#FFFFFF";
  const perceived = (rgb.r * 0.299 + rgb.g * 0.587 + rgb.b * 0.114) / 255;
  return perceived > 0.36 ? "#000000" : "#FFFFFF";
}

/**
 * For high-saturation themes (e.g. Pulse red), the readable text may
 * still fall below the AA threshold. In that case, return a dark-overlay
 * rgba string that can be composited on top of the bg to push contrast
 * past 4.5:1 without losing the brand hue. Returns `null` when no
 * overlay is needed.
 */
export function shellOverlayIfNeeded(hexBg, textHex) {
  const ratio = contrastRatio(textHex, hexBg);
  if (ratio >= WCAG_AA_NORMAL) return null;
  // Start at 12% alpha and step up until contrast clears 4.5:1.
  // We darken towards black when text is white; lighten when text is dark.
  const darken = textHex === "#FFFFFF";
  for (let a = 0.12; a <= 0.55; a += 0.08) {
    const mixed = mixHex(hexBg, darken ? "#000000" : "#FFFFFF", a);
    if (contrastRatio(textHex, mixed) >= WCAG_AA_NORMAL) {
      return `rgba(${darken ? "0,0,0" : "255,255,255"},${a.toFixed(2)})`;
    }
  }
  // Last resort — return a strong overlay even if we couldn't quite hit AA;
  // it still meaningfully improves legibility.
  return `rgba(${darken ? "0,0,0" : "255,255,255"},0.55)`;
}

function mixHex(a, b, alpha) {
  const ra = hexToRgb(a), rb = hexToRgb(b);
  if (!ra || !rb) return a;
  const r = Math.round(ra.r * (1 - alpha) + rb.r * alpha);
  const g = Math.round(ra.g * (1 - alpha) + rb.g * alpha);
  const bl = Math.round(ra.b * (1 - alpha) + rb.b * alpha);
  return `#${[r, g, bl].map((x) => x.toString(16).padStart(2, "0")).join("")}`;
}
